/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*
* A workbench for exercising form field components against in-memory data, without having to drive
* a production page to do it. Not linked from any menu - reached by typing the URL.
*
* It is an ordinary form page, so the list view exercises the table rendering of each component
* and clicking a row exercises the form rendering, with no scaffolding of its own.
*
* Add a field per component under test. Keep them independent, so one experiment can never
* break another.
*/
import { Component, inject } from '@angular/core';
import { of } from 'rxjs';
import { Person } from '@src/app/model/person';
import { Unit } from '@src/app/model/unit';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { FormPickLinkComponent } from '@src/app/shared/form/form-pick-link/form-pick-link.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { FormDateTimeComponent } from '@src/app/shared/form/form-date-time/form-date-time.component';
import { FormEmailComponent } from '@src/app/shared/form/form-email/form-email.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { FormRichTextComponent } from '@src/app/shared/form/form-rich-text/form-rich-text.component';
import { FormCheckboxComponent } from '@src/app/shared/form/form-checkbox/form-checkbox.component';
import { FormPhoneComponent } from '@src/app/shared/form/form-phone/form-phone.component';
import { FormDurationComponent } from '@src/app/shared/form/form-duration/form-duration.component';
import { FormUrlComponent } from '@src/app/shared/form/form-url/form-url.component';
import { FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormComboBoxComponent } from '@src/app/shared/form/form-combo-box/form-combo-box.component';
import { FormPillComponent } from '@src/app/shared/form/form-pill/form-pill.component';
import { channelState } from '@src/app/modules/social/channelPillFields';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { FormButtonComponent } from '@src/app/shared/form/form-button/form-button.component';
import { Field } from '@src/app/shared/field/Field';
import { GridField } from '@src/app/shared/grid/grid-field';
import { MessageService } from '@src/app/shared/message.service';
import { FormConfig } from '@src/app/shared/form/FormConfig';
import { PersonPageNavRoute } from '@src/app/routes/person-page-nav';
import { UnitPageNavRoute } from '@src/app/routes/unit-page';
import { FieldLabNavRoute } from '@src/app/routes/field-lab-nav';
import {
    LabContactService, LabItem, LabItemService, LabUnitService,
    LAB_CATEGORIES, LAB_OWNERS, LAB_PRIORITIES, LAB_STATUSES
} from '@src/app/pages/field-lab/lab-data';
import {
    MakeFieldsDisabledAction, MakeFieldsEditableAction, MakeFieldsReadonlyAction
} from '@src/app/pages/field-lab/field-state-actions';

@Component({
    selector: 'app-field-lab',
    templateUrl: './field-lab.component.html',
    imports: [FormPageComponent]
})
export class FieldLabComponent extends AbstractPageComponent {
    dataSvc = inject(LabItemService);
    private msgSvc = inject(MessageService);

    /*
     * PickLink. Unit is the regular variant, sized like a mat-form-field, and Contact is the
     * condensed one, so both renderings are on screen at once.
     *
     * The unit picker takes no filter - on a real page forceTeamId already restricts the list to
     * the right OMC. The contact picker narrows further, to the unit currently on the form, which
     * is what proves the filter is resolved on click rather than maintained.
     */
    unitField = FormPickLinkComponent.make({
        label: 'Unit',
        value: 'unitId',
        // Composed client side from the nested unit, so the label reads the same whether it came
        // from the loaded record or straight off the row just picked
        textValue: (o: LabItem) => Unit.getFullName(o.unit),
        nav: UnitPageNavRoute,
        refreshes: [() => this.contactField.clear()],
        pick: {
            service: inject(LabUnitService),
            config: () => Unit.pickDialogFormConfig(),
            textFrom: (u: Unit) => Unit.getFullName(u),
            allowNull: true,
        },
    }, { formColumn: 1 });   // regular - sits among the standard height inputs

    contactField = FormPickLinkComponent.make({
        label: 'Contact',
        value: 'personId',
        textValue: 'personName',
        nav: PersonPageNavRoute,
        pick: {
            service: inject(LabContactService),
            config: () => Person.pickDialogFormConfig(),
            filter: (o: LabItem) => o.unitId ? { unitId: o.unitId as string } : {},
            textFrom: (p: Person) => p.fullName,
            allowNull: true,
        },
    }, { formColumn: 5, condensed: true });

    /* Columns 1 and 2 - the standard full height inputs */
    textField = FormTextComponent.make('Title', 'title');
    dateField = FormDateComponent.make('Start Date', 'startDate');
    checkboxField = FormCheckboxComponent.make('Active', 'active');
    phoneField = FormPhoneComponent.make('Phone', 'phone');

    dateTimeField = FormDateTimeComponent.make('Last Seen', 'lastSeenAt', { formColumn: 4 });
    emailField = FormEmailComponent.make('Email', 'email', { formColumn: 4 });
    numberField = FormNumberComponent.make('Quantity', 'quantity', {}, { formColumn: 4 });
    currencyField = FormNumberComponent.makeCurrency('Amount', 'amount', { formColumn: 4 });
    percentField = FormNumberComponent.makePercent('Rate', 'rate', { formColumn: 4 });
    durationField = FormDurationComponent.make('Time Spent', 'timeSpent', { formColumn: 4 });

    /* Column 3 - the choosers, alongside the text area */
    notesField = FormTextAreaComponent.make('Notes', 'notes', { formColumn: 3 });
    iconButtonField = FormButtonComponent.makeIconButton('Icon Button', 'preview',
        'Testing form-mode icon button spacing', () => this.msgSvc.show('Icon button clicked'), 'Test Button')
        .override({ formColumn: 3 });
    urlField = FormUrlComponent.make('Website', 'website', { formColumn: 3 });
    picklistField = FormPicklistComponent.make('Status', 'statusId', null,
        { items: LAB_STATUSES }, { formColumn: 3 });
    comboField = FormComboBoxComponent.make('Category', 'categoryId', null,
        { items: LAB_CATEGORIES, allowSelectNone: true }, { formColumn: 3 });
    radioField = FormPicklistComponent.make('Priority', 'priorityId', null,
        { items: LAB_PRIORITIES, asRadio: true }, { formColumn: 3 });

    /* Column 2 - rich text needs the room */
    detailsField = FormRichTextComponent.make('Details', 'details', { formColumn: 2 });

    /*
     * Column 5 - the condensed style used down the last column of the request and unit pages.
     * The contact PickLink lives here rather than beside the unit one, so the two can be compared
     * in a normal column and in a condensed one.
     */
    ownerField = FormPicklistComponent.make('Owner', 'ownerId', null,
        { items: LAB_OWNERS }, { formColumn: 5, condensed: true });
    reviewDateField = FormDateComponent.make('Review Date', 'reviewDate',
        { formColumn: 5, condensed: true });
    approvedAtField = FormDateTimeComponent.make('Approved', 'approvedAt',
        { formColumn: 5, condensed: true, readonly: true });
    balanceField = FormNumberComponent.makeCurrency('Balance', 'balance',
        { formColumn: 5, condensed: true });      
    channelField = FormPillComponent.make('Channel', 'channelName', {
        makeOption: (o: LabItem) => channelState(o.channelName, o.channelName, o.channelBadge),
        formClick: () => {
            this.msgSvc.show('Channel clicked - a real page would open a dialog here');
            return of(null);
        },
    }).override({ formColumn: 5 });
    navLinkField = FormNavLinkComponent.makeDetail('Unit Link', 'unitName', 'unitId', UnitPageNavRoute)
        .override({ formColumn: 5 });
    textButtonField = FormButtonComponent.makeTextButton('Work Order', 'workOrder',
        () => this.msgSvc.show('Button clicked - a real page would open an editor here'),
        { formColumn: 5, visible: Field.formOnly });

    entriesGrid = new GridField({
        field: {
            value: 'entries', cellOpts: { heading: 'Entries' }, tabIcon: 'lists',
            visible: Field.formOnly, sendServer: false,
        },
        rowFactory: () => [
            FormTextComponent.make('Title', 'title'),
            FormDateComponent.make('Date', 'entryDate'),
            FormNumberComponent.make('Quantity', 'quantity'),
            FormPicklistComponent.make('Status', 'statusId', null, { items: LAB_STATUSES }),
            FormCheckboxComponent.make('Done', 'done'),
        ],
    });

    config = new FormConfig({
        navRoute: FieldLabNavRoute,
        title: $localize`Field Lab`,
        help: $localize`Workbench for trying out form field components`,
        fieldSet: new FieldSet({
            fields: [
                FieldMaker.id(),
                FieldMaker.rev(),
                this.textField,
                this.dateField,
                this.checkboxField,
                this.phoneField,

                this.detailsField,

                this.notesField,
                this.iconButtonField,
                this.urlField,
                this.picklistField,
                this.comboField,
                this.radioField,

                this.dateTimeField,
                this.emailField,
                this.numberField,
                this.currencyField,
                this.percentField,
                this.durationField,

                this.unitField,
                this.contactField,
                this.ownerField,
                this.reviewDateField,
                this.approvedAtField,
                this.balanceField,
                this.navLinkField,
                this.textButtonField,
                this.channelField,
            ],
            formLayout: LAYOUT_OPTIONS.fiveCol,
        }),
        tabFields: [this.entriesGrid],
        service: this.dataSvc,
        allowNew: false,
        allowDelete: false,
        actions: [
            new MakeFieldsEditableAction(),
            new MakeFieldsReadonlyAction(),
            new MakeFieldsDisabledAction(),
        ],
    });

}
